import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, FileText, Info, Sparkles, Tag, Wrench, X, type LucideIcon } from 'lucide-react';
import { useAgency } from '@/auth/AgencyProvider';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { dateInZone } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { localizedLink, localizedText, type Announcement, type AnnouncementKind } from '@/data/useAnnouncements';

/**
 * One announcement, shared by the banner and the inbox (fleet-driver-pay design 7.4). The kind sets the
 * kicker (a Badge with words, never the tone alone), its icon and tone: info is info, maintenance warning
 * (with the warning left border, as the banner always had), terms and pricing muted ink, release the brand
 * wash (product news is brand, not meaning). Then the title, the body with its line breaks, the optional
 * call to action to a portal route in the reader's language, and the window in the agency's time zone.
 * The banner passes `onDismiss` (close button); the inbox passes `unread` + `onMarkRead`.
 */

const KIND: Record<AnnouncementKind, { icon: LucideIcon; tone: BadgeTone; iconClass: string }> = {
  info: { icon: Info, tone: 'info', iconClass: 'text-info' },
  maintenance: { icon: Wrench, tone: 'warning', iconClass: 'text-warning' },
  terms: { icon: FileText, tone: 'muted', iconClass: 'text-ink-muted' },
  pricing: { icon: Tag, tone: 'muted', iconClass: 'text-ink-muted' },
  release: { icon: Sparkles, tone: 'brand', iconClass: 'text-accent-base' },
};

export interface AnnouncementCardProps {
  row: Announcement;
  /** `ended` changes the date line to "ended <date>" (inbox only). */
  state?: 'active' | 'ended';
  /** Banner: a dismissible notice's close button. */
  onDismiss?: () => void;
  /** Inbox: the row is unread (shows the mark and Mark as read). */
  unread?: boolean;
  onMarkRead?: () => void;
  busy?: boolean;
  headingLevel?: 2 | 3;
  className?: string;
}

export function AnnouncementCard({ row, state = 'active', onDismiss, unread = false, onMarkRead, busy = false, headingLevel = 2, className }: AnnouncementCardProps) {
  const { t, i18n } = useTranslation();
  const agency = useAgency();
  const timeZone = agency.status === 'ready' ? agency.agency.timeZone : undefined;
  const language = i18n.resolvedLanguage;
  const locale = language ?? 'en';
  const { title, body } = localizedText(row, language);
  const link = localizedLink(row, language);
  const kind = KIND[row.kind] ?? KIND.info;
  const Icon = kind.icon;
  const Heading = `h${headingLevel}` as const;
  const dates = t(state === 'ended' ? 'announcements.endedOn' : 'announcements.activeUntil', {
    start: dateInZone(row.starts_at, timeZone, locale),
    end: dateInZone(row.ends_at, timeZone, locale),
  });

  return (
    <section
      aria-label={title}
      data-kind={row.kind}
      className={cn(
        'flex gap-3 rounded-md border bg-card px-4 py-3 shadow-card',
        row.kind === 'maintenance' ? 'border-warning border-l-4' : 'border-hairline',
        className,
      )}
    >
      <Icon data-icon={row.kind} className={cn('mt-0.5 hidden size-5 shrink-0 sm:block', kind.iconClass)} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge label={t(`announcements.kind.${row.kind}`)} tone={kind.tone} size="sm" />
          {unread && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink">
              <span className="size-2 rounded-full bg-accent-base" aria-hidden="true" />
              {t('announcements.unread')}
            </span>
          )}
        </div>
        <Heading className={cn('text-base text-ink', unread || !onMarkRead ? 'font-semibold' : 'font-medium')}>{title}</Heading>
        <p className="whitespace-pre-line text-base text-ink-secondary">{body}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2">
          {link && (
            <Link
              to={link.path}
              className="inline-flex min-h-touch items-center gap-1 text-sm font-medium text-accent-base hover:underline focus-visible:underline sm:min-h-0"
            >
              {link.label}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          )}
          <span className="tabular-figures text-xs text-ink-muted">{dates}</span>
          {unread && onMarkRead && (
            <Button type="button" variant="ghost" size="sm" className="ml-auto" disabled={busy} onClick={onMarkRead}>
              <Check className="size-4" aria-hidden="true" />
              {t('announcements.markRead')}
            </Button>
          )}
        </div>
      </div>
      {onDismiss && (
        <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={onDismiss} aria-label={t('announcements.dismiss')}>
          <X className="size-4" aria-hidden="true" />
        </Button>
      )}
    </section>
  );
}
